import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Coins, Gift, CreditCard, ArrowLeft, Sparkles } from "lucide-react";
import { CircuitBg } from "@/components/CircuitBg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Lista de precios pública: cualquiera puede verla y compartirla. */
export default function Pricing() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/pricing`).then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);

  return (
    <div className="min-h-screen theme-noir relative" style={{ backgroundColor: "#05070C" }}
      data-testid="pricing-page">
      <CircuitBg />
      <div className="relative max-w-5xl mx-auto px-6 py-14">
        <Link to="/acceso" data-testid="pricing-back-link"
          className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> Volver
        </Link>

        <div className="mt-8 mb-12">
          <img src="/logo-digital-forge.jpg" alt="Digital Forge"
            className="w-24 h-24 rounded-full object-cover border border-white/10 brand-hue is-logo" />
          <div className="font-heading text-[11px] tracking-[0.42em] text-amber-300/90 mt-6">DIGITAL FORGE</div>
          <h1 className="cover-title text-4xl sm:text-5xl lg:text-6xl mt-2">PRECIOS CLAROS</h1>
          <p className="text-base md:text-lg text-white/70 mt-4 max-w-xl leading-relaxed">
            Pagas solo lo que usas con créditos. Sin mensualidades, sin letras chiquitas.
          </p>
        </div>

        {data === null && <Loader2 className="w-7 h-7 animate-spin text-amber-300" />}
        {data === false && <p className="text-white/60">No pudimos cargar los precios. Intenta de nuevo.</p>}

        {data && (
          <>
            <div className="glass-card forge-card p-6 mb-10 flex flex-wrap items-center gap-x-10 gap-y-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-white/45">Valor del crédito</div>
                <div className="text-2xl font-bold text-white mt-1">
                  1 crédito = ${data.credit_price_mxn} MXN
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/75">
                <Gift className="w-4 h-4 text-amber-300" />
                {data.welcome_credits} créditos de regalo al entrar
              </div>
              <div className="flex items-center gap-2 text-sm text-white/75">
                <Sparkles className="w-4 h-4 text-amber-300" />
                +{data.referral_credits} créditos por cada amigo que invites
              </div>
            </div>

            <h2 className="text-base md:text-lg font-heading text-white mb-4">Lo que cuesta cada cosa</h2>
            <div className="glass-card overflow-hidden mb-14" data-testid="pricing-operations">
              {data.operations.map((op, i) => (
                <div key={op.label} data-testid={`pricing-row-${i}`}
                  className={`flex items-center justify-between px-5 py-4 ${i % 2 ? "bg-white/[0.02]" : ""}`}>
                  <span className="text-sm text-white/85">{op.label}</span>
                  <span className="flex items-baseline gap-3">
                    <span className="text-[11px] text-white/45 font-mono">{op.credits} cr</span>
                    <span className="text-base font-semibold text-amber-300 font-mono">${op.mxn} MXN</span>
                  </span>
                </div>
              ))}
            </div>

            <h2 className="text-base md:text-lg font-heading text-white mb-4">Paquetes de créditos</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pricing-packs">
              {data.packs.map((p) => (
                <div key={p.credits} className="glass-card forge-card p-6" data-testid={`pricing-pack-${p.credits}`}>
                  <Coins className="w-5 h-5 text-amber-300" />
                  <div className="text-2xl font-bold text-white mt-4">{p.credits}</div>
                  <div className="text-[11px] text-white/45 uppercase tracking-wider">créditos</div>
                  <div className="text-lg text-amber-300 font-mono mt-4">
                    ${p.price_mxn.toLocaleString()} MXN
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-14 glass-card forge-card p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
              <div>
                <div className="text-white font-semibold flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-amber-300" /> Pago con tarjeta, seguro
                </div>
                <p className="text-sm text-white/60 mt-1.5">
                  Los créditos se abonan solos en cuanto el pago se confirma.
                </p>
              </div>
              <Link to="/registro" data-testid="pricing-cta-btn"
                className="text-center text-sm font-semibold px-7 py-3 rounded-full text-white transition-transform hover:-translate-y-0.5"
                style={{ background: "linear-gradient(100deg,#C2620E,#FF8A2B)" }}>
                Crear mi cuenta
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
